import {
  objectFitCoverImageStyles,
  SDUIImage,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Image/SDUIImage";
import { getCssAlignmentValue } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/getCssAlignmentValue";
import {
  convertServerResponsivePropertyToClientResponsiveProperty,
  getResponsiveValueAtIndex,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ComponentLoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIBannerTextContent } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Banner/__internal__/BannerTextContent/SDUIBannerTextContent";
import {
  assignInlineVars,
  DynamicVariable,
} from "@faire/retailer-visitor-shared/lib/tailwindUtils";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { cn } from "@faire/web/tools/cn";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { IBannerComponent } from "@faire/web-api--source/indigofair/data/IBannerComponent";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React from "react";

export type SDUIImageBannerV2Props = {
  bannerComponent: IBannerComponent;
  loggingData: ComponentLoggingEventParams;
  renderBehaviour?: SDUIComponentRenderBehaviour;
  // used to share logic with INLINE_BANNER variant of SDUI banner
  inline?: boolean;
};

const varMinHeightMobile: DynamicVariable<"--f-sdui-ib-min-h-m"> = {
  variable: "--f-sdui-ib-min-h-m",
  className: "min-h-(--f-sdui-ib-min-h-m)",
};

const varMinHeightTablet: DynamicVariable<"--f-sdui-ib-min-h-md"> = {
  variable: "--f-sdui-ib-min-h-md",
  className: "md:min-h-(--f-sdui-ib-min-h-md)",
};

const varMinHeightDesktop: DynamicVariable<"--f-sdui-ib-min-h-lg"> = {
  variable: "--f-sdui-ib-min-h-lg",
  className: "lg:min-h-(--f-sdui-ib-min-h-lg)",
};

const IMAGE_BANNER_HEIGHTS: ResponsiveValues<number> = {
  mobileAndAbove: 200,
  tabletAndAbove: 320,
  desktopAndAbove: 400,
};

export const SDUIImageBannerV2Renderer = ({
  bannerComponent,
  loggingData,
  renderBehaviour,
  inline = false,
}: SDUIImageBannerV2Props) => {
  const { images, responsive_images, vertical_content_alignment } =
    bannerComponent;

  const responsiveImages =
    convertServerResponsivePropertyToClientResponsiveProperty(
      images,
      (img) => img,
      responsive_images
    );

  return (
    <div
      className={cn(
        inline ? "relative w-full" : "relative w-full",
        !inline ? varMinHeightMobile.className : undefined,
        !inline ? varMinHeightTablet.className : undefined,
        !inline ? varMinHeightDesktop.className : undefined,
        // TODO - padding should be defined in the component width instead of applying
        // view-specific styling. Need to revisit component width definitions.
        // this is specifically used to align the promotional banner on the homepage
        // with the rest of page content
        loggingData.view_display_page === DisplayPageType.HOMEPAGE
          ? "lg:mx-16 lg:my-0 lg:w-[calc(100%-(var(--spacing)*32))]"
          : undefined
      )}
      style={assignInlineVars({
        [varMinHeightMobile.variable]: `${IMAGE_BANNER_HEIGHTS.mobileAndAbove}px`,
        [varMinHeightTablet.variable]: `${IMAGE_BANNER_HEIGHTS.tabletAndAbove}px`,
        [varMinHeightDesktop.variable]: `${IMAGE_BANNER_HEIGHTS.desktopAndAbove}px`,
      })}
    >
      <SDUIImage
        responsiveImage={getResponsiveValueAtIndex(
          responsiveImages,
          0,
          IImage.build()
        )}
        optimizeImageOptions={{
          mobileAndAbove: { height: IMAGE_BANNER_HEIGHTS.mobileAndAbove },
          tabletAndAbove: { height: IMAGE_BANNER_HEIGHTS.tabletAndAbove },
          desktopAndAbove: { height: IMAGE_BANNER_HEIGHTS.desktopAndAbove },
        }}
        className="absolute top-0 left-0 z-0 h-full w-full"
        imageStyles={objectFitCoverImageStyles}
        renderBehaviour={renderBehaviour}
        showPlaceholder={!inline}
      />
      <div
        className={cn(
          "flex min-h-[inherit] max-w-[1376px] flex-col",
          inline ? undefined : "mx-auto my-0 p-4 md:p-16 xl:p-12"
        )}
        style={{
          justifyContent: `${getCssAlignmentValue(
            vertical_content_alignment,
            "center"
          )}`,
        }}
      >
        <SDUIBannerTextContent
          className="relative z-[1] items-center text-center md:w-1/2 md:items-start md:text-left"
          data-test-id="image-banner-v2-content"
          bannerComponent={bannerComponent}
          loggingData={loggingData}
        />
      </div>
    </div>
  );
};
